/**
 * One of two hand-written slices in the console (the other is the client switcher).
 *
 * Auth here is one password (BIBLE §9): the operator types it, `POST /api/auth/login`
 * confirms it, and from then on it rides in a header on every request. There is no token
 * to mint, refresh or expire, so there is no state to model beyond "do we have the key".
 *
 * Session storage, not local: closing the tab logs out.
 */

import { createSlice, type PayloadAction } from '@reduxjs/toolkit'

const STORAGE_KEY = 'voe.operator-key'

export interface AuthState {
  key: string | null
}

function readStoredKey(): string | null {
  try {
    return sessionStorage.getItem(STORAGE_KEY)
  } catch {
    // Private modes and locked-down browsers throw rather than returning null. Losing the
    // key costs one login; refusing to render the console costs the whole session.
    return null
  }
}

export function persistKey(key: string | null): void {
  try {
    if (key === null) sessionStorage.removeItem(STORAGE_KEY)
    else sessionStorage.setItem(STORAGE_KEY, key)
  } catch {
    /* see above */
  }
}

const authSlice = createSlice({
  name: 'auth',
  initialState: (): AuthState => ({ key: readStoredKey() }),
  reducers: {
    loggedIn(state, action: PayloadAction<string>) {
      state.key = action.payload
    },
    loggedOut(state) {
      state.key = null
    },
  },
  selectors: {
    selectKey: (state) => state.key,
    selectIsAuthenticated: (state) => state.key !== null,
  },
})

export const { loggedIn, loggedOut } = authSlice.actions
export const { selectKey, selectIsAuthenticated } = authSlice.selectors
export default authSlice.reducer
